<template>
  <div v-show="showBroadcastCommands">
    <q-card square flat bordered class="my-card bg-grey-1 vertical-bottom">
      <q-toolbar>
        <q-toolbar-title class="text-weight-bold text-grey-brand"
          >Broadcast Commands</q-toolbar-title
        >
        <q-btn
          flat
          round
          dense
          icon="close"
          @click="onBroadcastCommandsCloseClick"
        />
      </q-toolbar>

      <!-- Select devices button. -->
      <q-card-section>
        <div class="column">
          <q-btn
            class="btn-brand"
            label="Select Agms"
            dense
            no-caps
            @click="onSelectDevicesButtonClick"
          />

          <!-- AGM selection summary -->
          <div
            v-if="selectedDevices.length > 0"
            class="q-mt-sm q-pa-sm"
            style="
              background: #f5f5f5;
              border-radius: 4px;
              font-size: 12px;
              line-height: 1.4;
            "
          >
            <span class="text-grey-7"
              >AGMs ({{ selectedDevices.length }}):</span
            >
            {{ truncatedSelectedDeviceLabels }}
            <a
              v-if="selectedDevices.length > 5"
              href="#"
              style="font-size: 11px"
              @click.prevent="showAllAgmsSummary = !showAllAgmsSummary"
            >
              {{ showAllAgmsSummary ? 'Show less' : 'Show all' }}
            </a>
          </div>
        </div>
      </q-card-section>

      <!-- Select commands button. -->
      <q-card-section>
        <div class="column">
          <q-btn
            class="btn-brand"
            label="Select Commands"
            dense
            no-caps
            @click="onSelectCommandsButtonClick"
          />

          <!-- Commands selection summary -->
          <div
            v-if="selectedCommandLabels.length > 0"
            class="q-mt-sm q-pa-sm"
            style="
              background: #f5f5f5;
              border-radius: 4px;
              font-size: 12px;
              line-height: 1.4;
            "
          >
            <span class="text-grey-7">Commands:</span>
            {{ selectedCommandLabels.join(', ') }}
          </div>
        </div>
      </q-card-section>

      <!-- Send commands now button. -->
      <q-card-section>
        <div class="column">
          <q-btn
            class="btn-brand"
            label="Send Commands Now"
            dense
            no-caps
            :disable="
              selectedDevices.length === 0 || selectedCommandLabels.length === 0
            "
            @click="onSendCommandsNowButtonClick"
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- Select devices dialog. -->
    <q-dialog v-model="showSelectDevicesDialog">
      <q-card style="width: 600px; max-width: 90vm">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Select Devices</q-toolbar-title
          >
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="column" style="max-height: 300px; overflow-y: auto">
            <div class="col q-pb-sm">
              <q-option-group
                v-model="selectedDevices"
                dense
                :options="allDeviceOptions"
                color="indigo-9"
                type="checkbox"
              />
            </div>
          </div>

          <q-separator />

          <q-card-actions align="right">
            <q-btn
              class="btn-brand"
              label="Save"
              type="button"
              no-caps
              @click="onSelectDevicesDialogSaveButtonClick"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Select commands dialog. -->
    <q-dialog v-model="showSelectCommandsDialog">
      <q-card style="width: 600px; max-width: 90vm">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Select Commands</q-toolbar-title
          >
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <q-markup-table>
            <thead>
              <tr>
                <th class="text-left">
                  <span class="text-bold">Command</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-left">Power mode (active)</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandPowerMode"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Power mode (low power)</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandPowerModeLowPower"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Settings - mag only</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandFrequencySettingsMagOnly"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Settings - mag + 22 Hz</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandFrequencySettingsElfMag"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Settings - 22 Hz Only</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandFrequencySettingsElfOnly"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Settings - all detectors off</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandFrequencySettingsAllDetectorsOff"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Format disk</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandFormatDisk"
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
              <tr>
                <td class="text-left">Download between dates</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="broadcastCommandRequestForDownload"
                    color="blue-brand"
                    @input="onBroadcastCommandRequestForDownloadCheckboxClick"
                  ></q-checkbox>
                </td>
              </tr>
              <tr v-if="broadcastCommandRequestForDownloadDisplayDateControls">
                <td></td>
                <!-- Request for download (start date). -->
                <td class="text-right">
                  <q-input
                    v-model="broadcastCommandRequestForDownloadStartDate"
                    dense
                    filled
                    label="Start Date (UTC)"
                  >
                    <template #prepend>
                      <q-icon name="event" class="cursor-pointer">
                        <q-popup-proxy
                          ref="qStartDateProxy"
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-date
                            v-model="
                              broadcastCommandRequestForDownloadStartDate
                            "
                            mask="YYYY-MM-DD HH:mm:ss"
                            @input="hideStartDateProxy"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                    <template #append>
                      <q-icon name="access_time" class="cursor-pointer">
                        <q-popup-proxy
                          ref="qStartTimeProxy"
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-time
                            v-model="
                              broadcastCommandRequestForDownloadStartDate
                            "
                            mask="YYYY-MM-DD HH:mm:ss"
                            format24h
                            @input="hideStartTimeProxy"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                  </q-input>
                </td>
              </tr>
              <tr v-if="broadcastCommandRequestForDownloadDisplayDateControls">
                <td></td>
                <!-- Request for download (end date). -->
                <td class="text-right">
                  <q-input
                    v-model="broadcastCommandRequestForDownloadEndDate"
                    dense
                    filled
                    label="End Date (UTC)"
                  >
                    <template #prepend>
                      <q-icon name="event" class="cursor-pointer">
                        <q-popup-proxy
                          ref="qEndDateProxy"
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-date
                            v-model="broadcastCommandRequestForDownloadEndDate"
                            mask="YYYY-MM-DD HH:mm:ss"
                            @input="hideEndDateProxy"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                    <template #append>
                      <q-icon name="access_time" class="cursor-pointer">
                        <q-popup-proxy
                          ref="qEndTimeProxy"
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-time
                            v-model="broadcastCommandRequestForDownloadEndDate"
                            mask="YYYY-MM-DD HH:mm:ss"
                            format24h
                            @input="hideEndTimeProxy"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                  </q-input>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn
            class="btn-brand"
            label="Save"
            no-caps
            @click="onSelectCommandsDialogSaveButtonClick"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Validation message dialog. -->
    <q-dialog v-model="showValidationMessageDialog">
      <q-card>
        <q-card-section>
          <div class="text-h6">{{ validationDialogTitle }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">{{
          validationDialogMessage
        }}</q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="OK" color="primary" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { hidePopupProxy } from '@/utils/QuasarProxyHelper';
import DateCommon from '@/utils/DateCommon';
import { Device } from '@/models/Device';
import { BroadcastCommandsRequest } from '@/models/BroadcastCommands/BroadcastCommandsRequest';
import PigRunService from '@/services/PigRunService';

export default defineComponent({
  name: 'BroadcastCommands',
  data() {
    return {
      // Checkbox command booleans
      broadcastCommandPowerMode: false,
      broadcastCommandPowerModeLowPower: false,
      broadcastCommandFrequencySettingsMagOnly: false,
      broadcastCommandFrequencySettingsElfMag: false,
      broadcastCommandFrequencySettingsElfOnly: false,
      broadcastCommandFrequencySettingsAllDetectorsOff: false,
      broadcastCommandFormatDisk: false,
      broadcastCommandRequestForDownload: false,
      broadcastCommandRequestForDownloadDisplayDateControls: false,
      broadcastCommandRequestForDownloadStartDate: '',
      broadcastCommandRequestForDownloadEndDate: '',
      // Dialogs
      showSelectDevicesDialog: false,
      showSelectCommandsDialog: false,
      showValidationMessageDialog: false,
      showAllAgmsSummary: false,
      // Selections
      selectedDevices: [] as number[],
      selectedBroadcastCommandArray: [] as string[],
      // Validation
      validationDialogTitle: '',
      validationDialogMessage: '',
    };
  },
  computed: {
    showBroadcastCommands(): boolean {
      return this.$store.state.showBroadcastCommands;
    },
    allDeviceOptions(): any[] {
      const allDeviceOptions: any[] = [];
      this.$store.state.device.devices.forEach((device: Device) => {
        allDeviceOptions.push({
          label: 'AGM UID ' + device.uid,
          value: device.uid,
        });
      });
      return allDeviceOptions;
    },
    selectedCommandLabels(): string[] {
      const labels: string[] = [];
      if (this.broadcastCommandPowerMode) labels.push('Power mode (active)');
      if (this.broadcastCommandPowerModeLowPower)
        labels.push('Power mode (low power)');
      if (this.broadcastCommandFrequencySettingsMagOnly)
        labels.push('Settings - mag only');
      if (this.broadcastCommandFrequencySettingsElfMag)
        labels.push('Settings - mag + 22 Hz');
      if (this.broadcastCommandFrequencySettingsElfOnly)
        labels.push('Settings - 22 Hz Only');
      if (this.broadcastCommandFrequencySettingsAllDetectorsOff)
        labels.push('Settings - all detectors off');
      if (this.broadcastCommandFormatDisk) labels.push('Format disk');
      if (this.broadcastCommandRequestForDownload)
        labels.push('Download between dates');
      return labels;
    },
    truncatedSelectedDeviceLabels(): string {
      const options = this.allDeviceOptions;
      const labels = this.selectedDevices.map((uid: number) => {
        const opt = options.find((o: any) => o.value === uid);
        return opt ? opt.label : 'UID ' + uid;
      });
      if (!this.showAllAgmsSummary && labels.length > 5) {
        return labels.slice(0, 5).join(', ') + ' ...';
      }
      return labels.join(', ');
    },
  },
  methods: {
    onBroadcastCommandsCloseClick() {
      this.$store.dispatch('showPigRunManager');
    },
    onSelectDevicesButtonClick() {
      this.showSelectDevicesDialog = true;
    },
    onSelectCommandsButtonClick() {
      this.showSelectCommandsDialog = true;
    },
    async onSendCommandsNowButtonClick() {
      if (
        this.selectedDevices == undefined ||
        this.selectedDevices.length == 0
      ) {
        this.validationDialogMessage = 'At least one AGM must be selected.';
        this.validationDialogTitle = 'Validation Message';
        this.showValidationMessageDialog = true;
        return;
      }

      let selectedFrequencySettingsCommandCount = 0;
      if (this.broadcastCommandFrequencySettingsMagOnly)
        selectedFrequencySettingsCommandCount++;
      if (this.broadcastCommandFrequencySettingsElfMag)
        selectedFrequencySettingsCommandCount++;
      if (this.broadcastCommandFrequencySettingsElfOnly)
        selectedFrequencySettingsCommandCount++;
      if (this.broadcastCommandFrequencySettingsAllDetectorsOff)
        selectedFrequencySettingsCommandCount++;

      if (selectedFrequencySettingsCommandCount > 1) {
        this.validationDialogMessage =
          'Only one settings broadcast command may be selected at a time.';
        this.validationDialogTitle = 'Validation Message';
        this.showValidationMessageDialog = true;
        return;
      }

      if (
        this.broadcastCommandPowerMode &&
        this.broadcastCommandPowerModeLowPower
      ) {
        this.validationDialogMessage =
          'The power mode (active) and power mode (low power) commands are mutually exclusive, only one may be selected at a time.';
        this.validationDialogTitle = 'Validation Message';
        this.showValidationMessageDialog = true;
        return;
      }

      if (
        this.broadcastCommandFormatDisk &&
        this.broadcastCommandRequestForDownload
      ) {
        this.validationDialogMessage =
          'The format disk and download between dates commands are mutually exclusive, only one may be selected at a time.';
        this.validationDialogTitle = 'Validation Message';
        this.showValidationMessageDialog = true;
        return;
      }

      this.selectedBroadcastCommandArray.length = 0;

      if (this.broadcastCommandPowerMode)
        this.selectedBroadcastCommandArray.push('PMA');
      if (this.broadcastCommandPowerModeLowPower)
        this.selectedBroadcastCommandArray.push('PMLP');
      if (this.broadcastCommandFrequencySettingsMagOnly)
        this.selectedBroadcastCommandArray.push('FSMAG');
      if (this.broadcastCommandFrequencySettingsElfMag)
        this.selectedBroadcastCommandArray.push('FSMAGELF');
      if (this.broadcastCommandFrequencySettingsElfOnly)
        this.selectedBroadcastCommandArray.push('FSELF');
      if (this.broadcastCommandFrequencySettingsAllDetectorsOff)
        this.selectedBroadcastCommandArray.push('FSOFF');
      if (this.broadcastCommandFormatDisk)
        this.selectedBroadcastCommandArray.push('FDISK');

      if (this.broadcastCommandRequestForDownload) {
        if (
          !DateCommon.IsDateAsStringValid(
            this.broadcastCommandRequestForDownloadStartDate
          )
        ) {
          this.validationDialogMessage =
            'If the download between dates command is selected, the associated start date must be specified.';
          this.validationDialogTitle = 'Validation Message';
          this.showValidationMessageDialog = true;
          return;
        }

        if (
          !DateCommon.IsDateAsStringValid(
            this.broadcastCommandRequestForDownloadEndDate
          )
        ) {
          this.validationDialogMessage =
            'If the download between dates command is selected, the associated end date must be specified.';
          this.validationDialogTitle = 'Validation Message';
          this.showValidationMessageDialog = true;
          return;
        }

        const startDate = DateCommon.ConvertDateAsStringToDate(
          this.broadcastCommandRequestForDownloadStartDate
        );
        const endDate = DateCommon.ConvertDateAsStringToDate(
          this.broadcastCommandRequestForDownloadEndDate
        );

        if (startDate.getTime() >= endDate.getTime()) {
          this.validationDialogMessage =
            'If the download between dates command is selected, the specified start date must be before the specified end date.';
          this.validationDialogTitle = 'Validation Message';
          this.showValidationMessageDialog = true;
          return;
        }

        this.selectedBroadcastCommandArray.push('DBLAR');
      }

      if (
        this.selectedBroadcastCommandArray == undefined ||
        this.selectedBroadcastCommandArray.length == 0
      ) {
        this.validationDialogMessage =
          'At least one broadcast command must be selected.';
        this.validationDialogTitle = 'Validation Message';
        this.showValidationMessageDialog = true;
        return;
      }

      let broadcastCommandsRequest: BroadcastCommandsRequest;

      if (this.broadcastCommandRequestForDownload) {
        const startDate = DateCommon.ConvertDateAsStringToDate(
          this.broadcastCommandRequestForDownloadStartDate
        );
        const endDate = DateCommon.ConvertDateAsStringToDate(
          this.broadcastCommandRequestForDownloadEndDate
        );
        broadcastCommandsRequest = new BroadcastCommandsRequest(
          this.selectedDevices,
          this.selectedBroadcastCommandArray,
          startDate,
          endDate
        );
      } else {
        broadcastCommandsRequest = new BroadcastCommandsRequest(
          this.selectedDevices,
          this.selectedBroadcastCommandArray,
          undefined,
          undefined
        );
      }

      await PigRunService.createBroadcastCommands(broadcastCommandsRequest)
        .then((response) => {
          this.validationDialogMessage =
            'Broadcast commands successfully sent.';
          this.validationDialogTitle = 'Information Message';
          this.showValidationMessageDialog = true;
        })
        .catch((exception) => {
          console.error(exception);
          if (exception.response.data != undefined) {
            this.validationDialogMessage =
              'Broadcast commands not successfully sent. LogId = [' +
              exception.response.data +
              '].';
            this.validationDialogTitle = 'Error Message';
            this.showValidationMessageDialog = true;
          }
        });
    },
    onSelectDevicesDialogSaveButtonClick() {
      this.showSelectDevicesDialog = false;
    },
    onSelectCommandsDialogSaveButtonClick() {
      this.showSelectCommandsDialog = false;
    },
    onBroadcastCommandRequestForDownloadCheckboxClick() {
      if (this.broadcastCommandRequestForDownload) {
        this.broadcastCommandRequestForDownloadDisplayDateControls = true;
      } else {
        this.broadcastCommandRequestForDownloadDisplayDateControls = false;
      }
    },
    hideStartDateProxy() {
      hidePopupProxy(this, 'qStartDateProxy');
    },
    hideStartTimeProxy() {
      hidePopupProxy(this, 'qStartTimeProxy');
    },
    hideEndDateProxy() {
      hidePopupProxy(this, 'qEndDateProxy');
    },
    hideEndTimeProxy() {
      hidePopupProxy(this, 'qEndTimeProxy');
    },
  },
});
</script>
